import { LeaveAreaCode } from '@gb-ticket/shared-types';
import { useState } from 'react';
import { Link } from 'react-router';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { PlatformPageHeader } from '@/platform/components/platform-page-header';
import { usePageTitle } from '@/hooks/use-page-title';
import { ApiRequestError } from '@/lib/api';
import { berlinTodayIso } from '@/products/leave/features/leave-calendar-utils';
import { leavePanelClassName } from '@/products/leave/features/leave-panel';
import {
  useGenerateLeaveHolidays,
  useLeaveAdminSetup,
} from '@/products/leave/features/use-leave-admin';
import { LeaveAreasPanel } from '@/products/leave/features/leave-areas-panel';
import { leaveAreaEnabled, useLeaveAreas } from '@/products/leave/features/use-leave-areas';
import { LEAVE_ADMIN_BALANCES_PATH } from '@/products/leave/leave-paths';
import { LeaveCompanyClosureForm } from '@/products/leave/pages/leave-company-closure-form';

function CheckRow({ label, ready }: { label: string; ready: boolean }) {
  return (
    <li className={`flex items-center justify-between gap-3 ${leavePanelClassName}`}>
      <span className="font-medium">{label}</span>
      <span className={ready ? 'text-sm text-emerald-700' : 'text-sm text-muted-foreground'}>
        {ready ? 'Fertig' : 'Offen'}
      </span>
    </li>
  );
}

export function LeaveAdminHomePage() {
  usePageTitle('HR-Verwaltung');
  const [closureOpen, setClosureOpen] = useState(false);
  const areas = useLeaveAreas();
  const closureOn = leaveAreaEnabled(areas.data, LeaveAreaCode.CLOSURE) !== false;
  const setup = useLeaveAdminSetup();
  const generate = useGenerateLeaveHolidays();
  const year = Number(berlinTodayIso().slice(0, 4));

  async function onPrepareYear() {
    try {
      const current = await generate.mutateAsync(year);
      const next = await generate.mutateAsync(year + 1);
      toast.success(
        `${current.created + next.created} Feiertage für ${year} und ${year + 1} angelegt.`,
      );
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : 'Die Feiertage konnten nicht erzeugt werden.',
      );
    }
  }

  return (
    <div className="space-y-6">
      <PlatformPageHeader
        title="HR-Verwaltung"
        description="Checkliste für Richtlinie, Feiertage und Arten. Die übrigen Seiten liegen im Tab-Band."
        action={
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              onClick={() => void onPrepareYear()}
              disabled={generate.isPending}
            >
              Jahr vorbereiten
            </Button>
            {closureOn ? (
              <Button
                type="button"
                variant="outline"
                onClick={() => setClosureOpen((open) => !open)}
              >
                Betriebsurlaub
              </Button>
            ) : null}
          </div>
        }
      />
      {closureOn && closureOpen ? <LeaveCompanyClosureForm /> : null}
      {setup.data ? (
        <ul className="space-y-3">
          <CheckRow label="Richtlinie" ready={setup.data.policyReady} />
          <CheckRow
            label={`Feiertage ${setup.data.year} und ${setup.data.nextYear}`}
            ready={setup.data.holidaysReady}
          />
          <CheckRow label="Arten" ready={setup.data.typesReady} />
          {setup.data.missingPersonnelCount !== null ? (
            <li className={leavePanelClassName}>
              <p className="font-medium">Personalnummern</p>
              <p className="mt-1 text-sm text-muted-foreground">
                {setup.data.missingPersonnelCount} aktive{' '}
                {setup.data.missingPersonnelCount === 1 ? 'Person hat' : 'Personen haben'} keine
                Personalnummer.{' '}
                <Link to={LEAVE_ADMIN_BALANCES_PATH} className="underline">
                  In den Salden nachtragen
                </Link>
                .
              </p>
            </li>
          ) : null}
        </ul>
      ) : (
        <p className="text-sm text-muted-foreground">Checkliste wird geladen…</p>
      )}
      <LeaveAreasPanel />
    </div>
  );
}
